import React, { useState, useEffect } from 'react';
import { gestionDocumentalAPI, textoErrorApi } from '../../api/api';
import VisorDocumento from '../VisorDocumento';
import { useAvisoTemporal, AvisoInline, DialogoConfirmar } from './RegistroHorarioEmpleado';
import { AVISO_SIMULACION } from '../../context/VistaEmpleadoContext';
import { AlertCircle, CheckCircle2, FileCheck, Upload, Camera, Eye, ExternalLink } from 'lucide-react';

// ==================== DOCUMENTOS PARA FIRMAR ====================
// (Antes, pestaña Firmas de Mi Portal; ahora la usa Mi Inicio > Documentos.)
// Documentos que el empleado debe firmar: los ve en el visor, los firma y sube
// el firmado (archivo o foto con la cámara del móvil), con vista previa antes
// de enviarlo. Los completados se ven también en el visor.

const TAMANO_MAXIMO_FIRMADO = 15 * 1024 * 1024;

// En el móvil un PDF dentro de un iframe no se puede leer: se abre en otra pestaña
const detectarMovil = () => {
  if (typeof window === 'undefined') return false;
  try {
    return window.innerWidth < 768 || !!window.matchMedia?.('(pointer: coarse)')?.matches;
  } catch (e) {
    return window.innerWidth < 768;
  }
};

const ESTADO_FIRMA_CONFIG = {
  Pendiente: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pendiente' },
  Descargado: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Visto' },
  Firmado: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'En revisión' },
  Validado: { bg: 'bg-green-100', text: 'text-green-800', label: 'Validado' },
  Rechazado: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rechazado' },
};

// `resaltarId`: asignación a destacar y enseñar (viene de Mis obligaciones).
// `soloLectura`: "Simular vista empleado"; se ve todo, pero no se sube ni se
// marca nada (los botones de subir enseñan un aviso).
const PestanaFirmas = ({ docsFirmar = [], onRecargar, resaltarId = null, soloLectura = false }) => {
  const { aviso, mostrarAviso, limpiarAviso } = useAvisoTemporal(6000);
  const [visor, setVisor] = useState(null);
  // Firmado elegido y pendiente de enviar: { doc, file, previewUrl, esImagen, esPdf }
  const [envio, setEnvio] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [errorEnvio, setErrorEnvio] = useState('');
  const [esMovil] = useState(detectarMovil);

  const previewUrl = envio?.previewUrl;
  useEffect(() => () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  useEffect(() => {
    if (!resaltarId) return;
    const el = document.querySelector(`[data-testid="firma-doc-${resaltarId}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [resaltarId, docsFirmar.length]);

  const pendientes = docsFirmar.filter(d => ['Pendiente', 'Descargado', 'Rechazado'].includes(d.estado));
  const completados = docsFirmar.filter(d => ['Firmado', 'Validado'].includes(d.estado));

  const verDocumento = async (doc) => {
    setVisor({ titulo: doc.nombre_documento, url: doc.archivo_original_url });
    // Abrirlo cuenta como descargado (el firmado se puede subir igualmente);
    // en la vista de simulación mirar no escribe nada
    if (!soloLectura && (doc.estado === 'Pendiente' || doc.estado === 'Rechazado')) {
      try {
        await gestionDocumentalAPI.marcarDescargado(doc.id);
        onRecargar();
      } catch (e) {
        console.error('Error marcando el documento como visto:', e);
      }
    }
  };

  const elegirArchivo = (doc, e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > TAMANO_MAXIMO_FIRMADO) {
      mostrarAviso('error', 'El archivo es demasiado grande. Máximo 15 MB.');
      return;
    }
    const esImagen = (file.type || '').startsWith('image/');
    const esPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    if (!esImagen && !esPdf) {
      mostrarAviso('error', 'Sube un PDF o una foto (JPG o PNG).');
      return;
    }
    setErrorEnvio('');
    setEnvio({ doc, file, previewUrl: window.URL.createObjectURL(file), esImagen, esPdf });
  };

  // Solo lectura: el clic en "Elegir archivo" / "Hacer foto" no abre el selector
  const bloquearSubida = (e) => {
    if (!soloLectura) return;
    e.preventDefault();
    mostrarAviso('error', AVISO_SIMULACION);
  };

  const confirmarEnvio = async () => {
    if (!envio) return;
    if (soloLectura) {
      setEnvio(null);
      mostrarAviso('error', AVISO_SIMULACION);
      return;
    }
    setSubiendo(true);
    setErrorEnvio('');
    try {
      await gestionDocumentalAPI.subirFirmado(envio.doc.id, envio.file);
      setEnvio(null);
      mostrarAviso('ok', 'Documento firmado enviado. Queda en revisión hasta que lo validen.');
      onRecargar();
    } catch (e) {
      console.error('Error subiendo el firmado:', e);
      setErrorEnvio(textoErrorApi(e.response?.data?.detail, 'No se ha podido subir el documento. Inténtalo de nuevo.'));
    } finally {
      setSubiendo(false);
    }
  };

  return (
    <>
      <AvisoInline aviso={aviso} onCerrar={limpiarAviso} className="mb-4" testId="firmas-aviso" />

      {/* Pendientes */}
      {pendientes.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-yellow-500" />
            Pendientes de firma ({pendientes.length})
          </h3>
          <p className="text-xs text-gray-500 mb-3">
            Abre el documento, fírmalo (en papel o en el móvil) y súbelo como PDF o con una foto.
          </p>
          <div className="space-y-3">
            {pendientes.map(doc => {
              const config = ESTADO_FIRMA_CONFIG[doc.estado] || ESTADO_FIRMA_CONFIG.Pendiente;
              return (
                <div key={doc.id} className={`border rounded-xl p-4 hover:shadow-md transition ${resaltarId === doc.id ? 'ring-2 ring-duetto-primary' : ''}`} data-testid={`firma-doc-${doc.id}`}>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900 text-sm">{doc.nombre_documento}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
                        {config.label}
                      </span>
                    </div>
                    {doc.tipo_documento && (
                      <span className="text-xs text-gray-500">{doc.tipo_documento.replace('_', ' ')}</span>
                    )}
                    {doc.fecha_limite && (
                      <span className="text-xs text-gray-400 ml-2">
                        Límite: {new Date(doc.fecha_limite).toLocaleDateString('es-ES')}
                      </span>
                    )}
                    {doc.notas_rechazo && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
                        <strong>Motivo de rechazo:</strong> {doc.notas_rechazo}
                      </div>
                    )}
                  </div>
                  {/* Móvil: "Hacer foto" primero y a todo el ancho; debajo Ver y Elegir archivo */}
                  <div className="grid grid-cols-2 gap-2 mt-3 sm:flex sm:flex-wrap">
                    {doc.archivo_original_url && (
                      <button
                        type="button"
                        onClick={() => verDocumento(doc)}
                        className="flex items-center justify-center gap-1.5 px-3 py-3 sm:py-2 bg-blue-600 text-white rounded-lg text-sm sm:text-xs font-medium hover:bg-blue-700 transition"
                        data-testid={`firmas-ver-${doc.id}`}
                      >
                        <Eye className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                        Ver documento
                      </button>
                    )}
                    <label
                      onClick={bloquearSubida}
                      aria-disabled={soloLectura || undefined}
                      data-uso="mi_inicio.subir_firmado"
                      data-uso-detalle="archivo"
                      className={`flex items-center justify-center gap-1.5 px-3 py-3 sm:py-2 bg-green-600 text-white rounded-lg text-sm sm:text-xs font-medium hover:bg-green-700 transition ${soloLectura ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'} ${doc.archivo_original_url ? '' : 'col-span-2'}`}
                    >
                      <Upload className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                      Elegir archivo
                      <input
                        type="file"
                        accept=".pdf,image/*"
                        className="hidden"
                        onChange={(e) => elegirArchivo(doc, e)}
                        disabled={soloLectura}
                        data-testid={`firmas-subir-${doc.id}`}
                      />
                    </label>
                    <label
                      onClick={bloquearSubida}
                      aria-disabled={soloLectura || undefined}
                      data-uso="mi_inicio.subir_firmado"
                      data-uso-detalle="foto"
                      data-tour="firmas-foto"
                      className={`col-span-2 order-first sm:order-none w-full sm:w-auto flex items-center justify-center gap-1.5 px-3 py-3 sm:py-2 bg-white border border-green-600 text-green-700 rounded-lg text-sm sm:text-xs font-medium hover:bg-green-50 transition ${soloLectura ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <Camera className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                      Hacer foto
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => elegirArchivo(doc, e)}
                        disabled={soloLectura}
                        data-testid={`firmas-foto-${doc.id}`}
                      />
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Completados */}
      {completados.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            Completados ({completados.length})
          </h3>
          <div className="space-y-2">
            {completados.map(doc => {
              const config = ESTADO_FIRMA_CONFIG[doc.estado] || ESTADO_FIRMA_CONFIG.Firmado;
              return (
                <div key={doc.id} className="border rounded-lg p-3 flex flex-wrap items-center justify-between gap-2 bg-gray-50">
                  <div>
                    <span className="text-sm text-gray-800">{doc.nombre_documento}</span>
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
                      {config.label}
                    </span>
                  </div>
                  {(doc.archivo_firmado_url || doc.drive_file_id) && (
                    <button
                      type="button"
                      onClick={() => setVisor({
                        titulo: `${doc.nombre_documento} (firmado)`,
                        url: doc.archivo_firmado_url || null,
                        driveId: doc.drive_file_id || null,
                      })}
                      className="px-3 py-2 text-sm sm:text-xs font-medium text-indigo-700 bg-white border border-indigo-200 rounded-lg hover:bg-indigo-50"
                      data-testid={`firmas-ver-firmado-${doc.id}`}
                    >
                      Ver firmado
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {docsFirmar.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <FileCheck className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p>No tienes documentos asignados para firmar</p>
        </div>
      )}

      <DialogoConfirmar
        abierto={!!envio}
        titulo="Enviar documento firmado"
        descripcion={envio ? `Comprueba que se lee bien antes de enviarlo: ${envio.doc.nombre_documento}.` : ''}
        textoConfirmar="Enviar firmado"
        ocupado={subiendo}
        error={errorEnvio}
        onConfirmar={confirmarEnvio}
        onCancelar={() => { setEnvio(null); setErrorEnvio(''); }}
        testId="firmas-confirmar-envio"
      >
        {envio && (
          <div className="space-y-2">
            <p className="text-xs text-gray-500 break-all">{envio.file.name}</p>
            <div className="border rounded-lg bg-gray-50 overflow-hidden">
              {envio.esImagen ? (
                <img src={envio.previewUrl} alt="Vista previa del documento firmado" className="max-h-[50vh] w-full object-contain" />
              ) : esMovil ? (
                <div className="p-4 flex flex-col items-center gap-3 text-center" data-testid="firmas-preview-movil">
                  <p className="text-sm text-gray-700 break-all">{envio.file.name}</p>
                  <a
                    href={envio.previewUrl}
                    target="_blank"
                    rel="noopener"
                    className="inline-flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg border border-duetto-primary text-duetto-primary bg-white text-sm font-medium"
                  >
                    <ExternalLink className="w-4 h-4" /> Abrir
                  </a>
                </div>
              ) : (
                <iframe src={envio.previewUrl} title="Vista previa del documento firmado" className="w-full h-[50vh] bg-white border-0" />
              )}
            </div>
          </div>
        )}
      </DialogoConfirmar>

      <VisorDocumento
        abierto={!!visor}
        onCerrar={() => setVisor(null)}
        titulo={visor?.titulo}
        url={visor?.url || undefined}
        driveId={visor?.driveId || undefined}
      />
    </>
  );
};

export default PestanaFirmas;
